import { memo, useMemo } from 'react';
import { useSelector } from '@bonanza-social/toolkit/state';
import { Box } from '@bonanza-social/moonlight';
import { selectCommunityJackpotEvents } from '../../../../state';
import { JackpotProgressBarProps } from './JackpotProgressBar.types';
import { getProgressHeight } from '../JackpotProgress.utils';
import CommunityJackpotImage from '../../../CommunityJackpotImage/CommunityJackpotImage';
import TotalContributions from './TotalContributions/TotalContributions';
import FlipReward from './FlipReward/FlipReward';
import Flare from './Flare/Flare';

function JackpotProgressBar({
  totalContributions = 0,
  milestones,
  activeMilestone,
  prevActiveMilestone,
  milestoneHit,
}: JackpotProgressBarProps) {
  const [event] = useSelector(selectCommunityJackpotEvents);
  const { progressBarBackgroundUrl } = event;
  const progressHeight = useMemo(
    () =>
      getProgressHeight(
        totalContributions,
        prevActiveMilestone?.threshold,
        activeMilestone?.threshold,
      ),
    [totalContributions, prevActiveMilestone, activeMilestone],
  );

  return (
    <Box
      position="absolute"
      width="space-1000"
      height="100%"
      top="-space-300"
      alignSelf="center"
      backgroundColor="mutedBackground"
      flexDirection="column-reverse"
      justifyContent="space-evenly"
      borderRadius="radius-600"
      padding="space-200"
      gap="space-300"
    >
      {milestones.map(({ id, status }, index) => {
        const isActive = id === activeMilestone?.id;
        const isClosed = status === 'RewardPending' || status === 'Completed';
        const height = isActive ? progressHeight : isClosed ? 100 : 0;

        return (
          <Box
            key={id}
            flex={1}
            justifyContent="flex-end"
            backgroundColor="screenBackground"
            borderRadius="radius-600"
            alignItems="center"
            {...(progressHeight === 100 && {
              shadowColor: 'darkSurfaceDisabled',
              shadowOpacity: 1,
              shadowRadius: 13,
            })}
            zIndex={isActive ? 1 : 0}
          >
            {isActive && (
              <>
                <FlipReward
                  activeMilestone={activeMilestone}
                  animatable={progressHeight === 100}
                />
                <TotalContributions
                  amount={
                    !milestoneHit
                      ? totalContributions
                      : activeMilestone?.threshold
                  }
                  animatable={progressHeight === 100}
                >
                  {progressHeight === 100 ? (
                    <Flare
                      variant={
                        index === milestones.length - 1 ? 'big' : 'small'
                      }
                    />
                  ) : null}
                </TotalContributions>
              </>
            )}
            <CommunityJackpotImage
              url={progressBarBackgroundUrl}
              fallback="communityJackpot.coinProgress"
              contentFit="cover"
              borderRadius="radius-600"
              height={`${height}%`}
              width="100%"
            />
          </Box>
        );
      })}
    </Box>
  );
}

export default memo(JackpotProgressBar);
